/**
 * Azure OpenAI Service
 * Handles chat completions with RAG pattern
 */

import { getAccessToken } from './authService';
import { getBackendHeaders } from './backendAuth';
import { getCurrentTraceId } from './webSearch/webSearchUtils';
import { azureConfig } from '@/config/azureConfig';
import type { ChatTurnTracer } from './chatTracing';
import {
  API_CONFIG,
  CITATION_INSTRUCTIONS,
  SYSTEM_PROMPTS,
  ERROR_MESSAGES,
} from '@/utils/constants';
import {
  buildCitationInstructions,
  type RetrievalSource,
} from '@/utils/citationInstructions';
import { applyTokenBudget, ensureTokenizer } from '@/utils/tokenBudget';
import { throwIfAborted } from '@/utils/cancellation';
import { buildUserContextNote } from './userContextService';
import {
  buildContextAdjustmentWarning,
  mergeWarnings,
} from '@/utils/contextAdjustmentWarning';
import type {
  ContentPart,
  OpenAIRequest,
  OpenAIResponse,
  OpenAIMessage,
  Message,
  MessageWarning,
  TokenUsage,
  EnhancedOpenAIResponse,
  ContextAdjustment,
  AzureOpenAIError,
} from '@/types';

const {
  endpoint: defaultEndpoint,
  deployment: defaultDeployment,
  apiVersion: defaultApiVersion,
} = azureConfig.openai;

// Hard cap on a single chat-completion attempt. Azure Model Router can
// route to o-series reasoning models that take minutes; even so, a
// frozen connection eventually has to surface as a typed timeout
// rather than a UI that looks locked up forever. The retry wrapper
// applies this per-attempt.
export const CHAT_COMPLETION_TIMEOUT_MS = 120_000;

/**
 * Classify error and extract structured information
 */
function classifyError(
  error: Error,
  status?: number,
  responseBody?: string,
  reasoningEffortRequested = false
): MessageWarning {
  // Parse Azure OpenAI error response if available
  let azureError: AzureOpenAIError | null = null;
  try {
    if (responseBody) {
      azureError = JSON.parse(responseBody) as AzureOpenAIError;
    }
  } catch {
    // Not valid JSON, continue with basic classification
  }

  // Word-boundary HTTP-code match against the error message. Prevents false
  // hits like a token count "503043" matching a bare "503" substring — that
  // collision previously misrouted context_length_exceeded (a 400) into the
  // service_error (5xx) branch, which is retryable, so the doomed oversized
  // request was sent 3× and surfaced as "service issues".
  const msgHasCode = (code: string): boolean =>
    new RegExp(`\\b${code}\\b`).test(error.message);

  // Context-window overflow (HTTP 400 context_length_exceeded). MUST be FIRST:
  // the Azure body embeds the token count (e.g. "...resulted in 503043
  // tokens...") whose digits can collide with the 429/5xx checks below. Keyed
  // primarily on the structured error code, with message-signal fallbacks.
  // Detect-by-signal (not status) so a locally thrown pre-flight overflow —
  // which carries no HTTP status — classifies the same way as Azure's.
  const overflowHaystack =
    `${responseBody ?? ''} ${error.message}`.toLowerCase();
  if (
    azureError?.error?.code === 'context_length_exceeded' ||
    overflowHaystack.includes('context_length_exceeded') ||
    overflowHaystack.includes('maximum context length') ||
    overflowHaystack.includes('exceed the configured limit') ||
    overflowHaystack.includes('reduce the length of the messages')
  ) {
    return {
      type: 'context_overflow',
      title: 'Conversation Too Long',
      message:
        'This conversation is too large for the model to process in a ' +
        'single request.',
      details: azureError?.error?.message || error.message,
      actionable: [
        'Start a new conversation to reset the context',
        'Remove large file attachments or shorten your message',
        'Long documents and search results add up quickly — ask a more ' +
          'specific question',
      ],
    };
  }

  // Rate limit (HTTP 429)
  if (status === 429 || msgHasCode('429')) {
    return {
      type: 'rate_limit',
      title: 'Rate Limit Exceeded',
      message: 'Too many requests in a short time period.',
      details: azureError?.error?.message || error.message,
      actionable: [
        'Wait a few moments before sending another message',
        'Consider upgrading your Azure OpenAI quota',
        'Reduce the frequency of requests',
      ],
    };
  }

  // Auth errors (HTTP 401/403)
  if (status === 401 || status === 403 ||
      msgHasCode('401') || msgHasCode('403')) {
    return {
      type: 'auth_error',
      title: 'Authentication Error',
      message: 'Unable to authenticate with Azure OpenAI.',
      details: azureError?.error?.message || error.message,
      actionable: [
        'Verify your API key or Azure AD credentials',
        'Check that your access token has not expired',
        'Contact your administrator if the issue persists',
      ],
    };
  }

  // Service errors (HTTP 500/502/503)
  if ((status && status >= 500) ||
      msgHasCode('500') ||
      msgHasCode('502') ||
      msgHasCode('503')) {
    return {
      type: 'service_error',
      title: 'Service Temporarily Unavailable',
      message: 'The Azure OpenAI service is experiencing issues.',
      details: azureError?.error?.message || error.message,
      actionable: [
        'Wait a few minutes and try again',
        'Check Azure Status page for service health',
        'Contact support if the issue persists',
      ],
    };
  }

  // Cancellation (user pressed Stop). Must come before the generic
  // network/timeout branches because AbortError surfaces as a plain
  // DOMException whose name is "AbortError" — its message string is
  // engine-dependent.
  if (error.name === 'AbortError' || error.message === 'cancelled') {
    return {
      type: 'cancelled',
      title: 'Generation Cancelled',
      message: 'You stopped the response before it finished.',
      details: error.message,
    };
  }

  // Timeout
  if (error.message.includes('timeout') || error.message.includes('timed out')) {
    return {
      type: 'timeout',
      title: 'Request Timed Out',
      message: 'The request took too long to complete.',
      details: error.message,
      actionable: [
        'Try sending a shorter message',
        'Reduce the max_tokens setting in preferences',
        'Check your network connection',
      ],
    };
  }

  // Token-limit cutoff before any visible text (common with reasoning models
  // when Max Tokens is too low). Distinct from content-filtering empties.
  if (error.message.includes('cut off at the token limit')) {
    return {
      type: 'truncated',
      title: 'Response Cut Off',
      message: 'The response hit the token limit before producing any text.',
      details: error.message,
      actionable: [
        'Increase "Max Tokens" in Settings (AI Settings)',
        'Reasoning models can spend the whole budget on internal reasoning',
        'Try a shorter or simpler request',
      ],
    };
  }

  // Empty response
  if (error.message.includes('empty response')) {
    return {
      type: 'empty_response',
      title: 'Empty Response Received',
      message: 'The AI returned an empty response.',
      details: error.message,
      actionable: [
        'This may be due to content filtering',
        'Try rephrasing your message',
        'Avoid sensitive or controversial topics',
      ],
    };
  }

  // Network error
  if (error.message.includes('network') || error.message.includes('fetch')) {
    return {
      type: 'network_error',
      title: 'Network Error',
      message: 'Unable to connect to Azure OpenAI.',
      details: error.message,
      actionable: [
        'Check your internet connection',
        'Verify the Azure OpenAI endpoint URL',
        'Check if a firewall is blocking the request',
      ],
    };
  }

  // Advanced thinking rejected. Two distinct 400s land here:
  //  (a) the deployment doesn't accept reasoning_effort at all
  //      (non-reasoning model, or a variant like o1-mini that omits it), or
  //  (b) it accepts the parameter but not the chosen level — some
  //      deployments only allow the default 'medium', so 'high' 400s.
  // Detect by the "reasoning" token, which Azure includes in both phrasings
  // ("Unsupported parameter: 'reasoning_effort'", "...does not support
  // 'high'..."). As a safety net for value-rejection wordings that might
  // omit the token, also treat any 400 as reasoning-related when we
  // actually sent reasoning_effort this turn.
  const haystack = `${responseBody ?? ''} ${error.message}`.toLowerCase();
  if (
    haystack.includes('reasoning') ||
    (reasoningEffortRequested && status === 400)
  ) {
    return {
      type: 'reasoning_unsupported',
      title: 'Advanced Thinking Not Supported',
      message:
        "This agent's model didn't accept the advanced-thinking request.",
      details: azureError?.error?.message || error.message,
      actionable: [
        'Turn off the Think toggle and send again, or',
        'In the agent\'s Azure AI tab, lower the thinking effort — some ' +
          'models only accept "medium"',
        'If the deployment isn\'t a reasoning model (o3, o3-mini, ' +
          'o4-mini, gpt-5), uncheck "supports reasoning"',
      ],
    };
  }

  // Generic API error
  return {
    type: 'api_error',
    title: 'API Error',
    message: 'An unexpected error occurred.',
    details: azureError?.error?.message || error.message,
    actionable: [
      'Try again in a moment',
      'Contact support if the issue persists',
    ],
  };
}

/**
 * Create warning from finish_reason
 */
function createWarningFromFinishReason(
  finishReason: string,
  usage: TokenUsage,
  maxTokens: number
): MessageWarning | undefined {
  switch (finishReason) {
    case 'length':
      return {
        type: 'truncated',
        title: 'Response Truncated',
        message: 'The response was cut off due to token limits.',
        details: `Reached maximum token limit of ${maxTokens}. ` +
                 `Used ${usage.completionTokens} completion tokens.`,
        actionable: [
          `Increase max tokens in preferences (current: ${maxTokens})`,
          'Ask a more specific question to get a complete answer',
          'Break your question into smaller parts',
        ],
      };

    case 'content_filter':
      return {
        type: 'content_filter',
        title: 'Content Filtered',
        message: 'The response was filtered by content policy.',
        details: 'Azure OpenAI detected potentially unsafe content.',
        actionable: [
          'Try rephrasing your message',
          'Avoid sensitive or controversial topics',
          'Review Azure OpenAI content filtering policies',
        ],
      };

    case 'stop':
      // Normal completion, no warning needed
      return undefined;

    default:
      console.warn(`[OpenAI Service] Unknown finish_reason: ${finishReason}`);
      return undefined;
  }
}

/**
 * Get chat completion from Azure OpenAI.
 *
 * `signal` lets callers cancel the in-flight request (e.g. user pressed
 * Stop). `timeoutMs` is an upper bound on this single attempt — when it
 * elapses the request is aborted with a typed `timeout` warning so the
 * UI can render a real error instead of staying on a frozen spinner.
 * The caller's `signal` and the internal timer share one
 * AbortController so either can win the race.
 */

/**
 * A one-line note giving the model the current date, time, and timezone.
 *
 * Without this, the model's only sense of "now" is its training cutoff, so
 * "today / latest / this year" framing and any date math are unreliable unless
 * a web/M365 search happens to supply the date. Prepended to every system
 * prompt and computed per request from the user's local clock. Uses a full
 * date + long time so the timezone name is included (e.g. "Tuesday, June 16,
 * 2026 at 4:37:00 PM Pacific Daylight Time").
 */
export function buildCurrentDateTimeNote(now: Date = new Date()): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'full',
    timeStyle: 'long',
  }).format(now);
  return `The current date and time is ${formatted}.`;
}

export async function getChatCompletion(
  messages: OpenAIMessage[],
  context?: string,
  agentConfig?: {
    endpoint: string;
    apiKey: string;
    deployment: string;
    apiVersion?: string;
  },
  aiConfig?: {
    temperature?: number;
    maxTokens?: number;
    reasoningEffort?: 'minimal' | 'low' | 'medium' | 'high';
    /** Deployment input context window (tokens) used to budget the request. */
    inputTokenLimit?: number;
    /**
     * What to do when history exceeds the input window: 'trim' (default) drops
     * the oldest turns; 'summarize' replaces them with an LLM-generated summary.
     */
    contextOverflowStrategy?: 'trim' | 'summarize';
    /**
     * Dedicated model for the 'summarize' strategy. `deployment` is required to
     * use it; endpoint/apiKey/apiVersion fall back to `agentConfig` when unset.
     */
    summaryModel?: {
      deployment: string;
      endpoint?: string;
      apiKey?: string;
      apiVersion?: string;
    };
  },
  customSystemPrompt?: string,
  activeSources?: RetrievalSource[],
  signal?: AbortSignal,
  timeoutMs: number = CHAT_COMPLETION_TIMEOUT_MS
): Promise<EnhancedOpenAIResponse> {
  // Use agent-specific config if provided, otherwise use defaults
  let endpoint = agentConfig?.endpoint || defaultEndpoint;
  const deployment = agentConfig?.deployment || defaultDeployment;
  const apiVersion = agentConfig?.apiVersion || defaultApiVersion;
  const apiKey = agentConfig?.apiKey;

  // Clean up endpoint - remove trailing slashes and any path components
  endpoint = endpoint.replace(/\/+$/, ''); // Remove trailing slashes
  // Remove any "/openai" suffix if present
  endpoint = endpoint.replace(/\/openai\/?$/, '');

  // Only get Azure AD token if no API key is provided
  const token = apiKey ? null : await getAccessToken();

  // Debug logging
  console.log('[OpenAI Service] Using config:', {
    endpoint,
    deployment,
    apiVersion,
    hasApiKey: !!apiKey,
    hasToken: !!token,
    hasAgentConfig: !!agentConfig,
  });

  // Build messages with system prompt.
  // Priority: Custom systemPrompt > Context-specific > Default.
  //
  // Citation instructions are dynamic: only sources that actually
  // contributed to this turn are enumerated in the rules block. When
  // `activeSources` is undefined (back-compat for unmigrated callers)
  // AND context is non-empty, we fall back to the static
  // CITATION_INSTRUCTIONS string so behavior matches the pre-refactor
  // path.
  const dynamicInstructions =
    activeSources !== undefined
      ? buildCitationInstructions(activeSources)
      : context
        ? CITATION_INSTRUCTIONS
        : '';
  const hasInstructions = dynamicInstructions.length > 0;
  const baseSystem = 'You are a helpful AI assistant for Stellar.';

  // The non-context portion of the system prompt. Kept separate from the
  // retrieval context so the budgeter can cap context independently while
  // always preserving the prompt + citation rules. The six branches below
  // reproduce the original composition exactly (context, when present, is
  // always appended last).
  let basePrompt: string;
  if (customSystemPrompt) {
    basePrompt =
      context && hasInstructions
        ? `${customSystemPrompt}\n\n${dynamicInstructions}`
        : customSystemPrompt;
  } else if (context) {
    basePrompt = hasInstructions
      ? `${baseSystem}\n\n${dynamicInstructions}`
      : baseSystem;
  } else {
    basePrompt = SYSTEM_PROMPTS.DEFAULT;
  }

  // Lead every system prompt (custom, context, or default) with ambient
  // context: the current date/time and — when known — who the user is and when
  // they were last here. Prepended ahead of citation rules + retrieval context.
  const ambientNotes = [buildCurrentDateTimeNote(), buildUserContextNote()]
    .filter(Boolean)
    .join('\n\n');
  basePrompt = `${ambientNotes}\n\n${basePrompt}`;

  // Determine API version-specific parameter handling
  // API versions with year >= 2025 have different parameter requirements
  const apiYear = parseInt(apiVersion.split('-')[0], 10);
  const useNewTokenParameter = apiYear >= 2025;
  let maxTokensValue = aiConfig?.maxTokens ?? API_CONFIG.OPENAI_MAX_TOKENS;
  // 2025+ (reasoning-capable) deployments can spend the entire completion
  // budget on internal reasoning tokens, yielding an empty `length`-finish
  // response when the cap is too low. Floor such deployments to at least the
  // default budget so there's always room to emit visible text. Larger
  // user-configured values are respected; legacy (<2025) deployments are
  // unaffected.
  if (useNewTokenParameter && maxTokensValue < API_CONFIG.OPENAI_MAX_TOKENS) {
    maxTokensValue = API_CONFIG.OPENAI_MAX_TOKENS;
  }

  // Budget the request to the deployment's input window BEFORE sending: cap
  // the retrieval context and trim oldest history with a sliding window. The
  // output cap (max_completion_tokens) does NOT constrain input, so without
  // this a long conversation or a large retrieval context 400s with
  // context_length_exceeded. The window is agent-declared; fall back to a
  // conservative default when unset.
  const inputLimit =
    aiConfig?.inputTokenLimit ?? API_CONFIG.DEFAULT_CONTEXT_WINDOW_TOKENS;

  // Load the exact tokenizer before budgeting so the trim/cap decisions use
  // real token counts (not the chars/4 heuristic, which under-counts dense
  // content). No-op in test mode and resolves instantly once loaded.
  await ensureTokenizer();

  // When the agent opts into summarization, replace the oldest turns that would
  // otherwise be trimmed with an LLM-generated summary so their substance is
  // carried forward. Falls back to plain trimming if the summary call fails.
  let messagesForBudget = messages;
  let summarizedMessages = 0;
  let summaryUsage: EnhancedOpenAIResponse['summaryUsage'];
  if (
    aiConfig?.contextOverflowStrategy === 'summarize' &&
    messages.length > 0
  ) {
    const dryRun = applyTokenBudget({
      basePrompt,
      context: context ?? '',
      messages,
      inputLimit,
      outputReserve: maxTokensValue,
    });
    if (dryRun.droppedMessages > 0) {
      const keptCount = dryRun.messages.length;
      const dropped = messages.slice(0, messages.length - keptCount);
      const kept = messages.slice(messages.length - keptCount);
      // Resolve the model used for summarization: a configured summaryModel
      // (e.g. a cheaper deployment) overrides the deployment, while its
      // endpoint/apiKey/apiVersion fall back to the agent's own config when
      // omitted. Unset → reuse the agent's model.
      const summaryModel = aiConfig?.summaryModel;
      const summaryAgentConfig = summaryModel?.deployment
        ? {
            endpoint: summaryModel.endpoint || agentConfig?.endpoint || '',
            apiKey: summaryModel.apiKey || agentConfig?.apiKey || '',
            deployment: summaryModel.deployment,
            apiVersion: summaryModel.apiVersion || agentConfig?.apiVersion,
          }
        : agentConfig;
      try {
        const summary = await summarizeConversation(
          dropped,
          summaryAgentConfig,
          inputLimit,
          signal
        );
        if (summary.content && summary.content.trim().length > 0) {
          messagesForBudget = [
            {
              role: 'system',
              content: `${CONVERSATION_SUMMARY_PREFIX}\n${summary.content}`,
            },
            ...kept,
          ];
          summarizedMessages = dropped.length;
          // Surface the summary call's usage + latency so the caller can
          // record an attributed usage_events row and a 'summarize' trace
          // phase. The model is the resolved summary deployment (cheaper
          // model when configured, else the agent's own).
          summaryUsage = {
            model: summaryAgentConfig?.deployment || deployment,
            servedModel: summary.servedModel,
            usage: summary.usage,
            latencyMs: summary.latencyMs,
          };
        }
      } catch (summaryError) {
        console.warn(
          '[OpenAI Service] Summarization failed; falling back to trim',
          summaryError
        );
      }
    }
  }

  const budget = applyTokenBudget({
    basePrompt,
    context: context ?? '',
    messages: messagesForBudget,
    inputLimit,
    outputReserve: maxTokensValue,
  });

  if (budget.contextTruncated) {
    console.warn(
      '[OpenAI Service] Retrieval context truncated to fit the input window'
    );
  }
  if (budget.droppedMessages > 0) {
    console.warn(
      `[OpenAI Service] Trimmed ${budget.droppedMessages} oldest message(s) ` +
        'to fit the input window'
    );
  }

  // Surface any reshape (trim/summarize/cap) so it's never silent.
  const contextAdjustment: ContextAdjustment | undefined =
    budget.droppedMessages > 0 ||
    budget.contextTruncated ||
    summarizedMessages > 0
      ? {
          trimmedMessages: budget.droppedMessages,
          contextTruncated: budget.contextTruncated,
          summarizedMessages,
        }
      : undefined;

  // Even after trimming to the newest turn the request still can't fit — fail
  // fast with an actionable, non-retryable error instead of paying for a
  // doomed round trip (multiplied by the retry count).
  if (budget.overflow) {
    const err = new Error(
      `${ERROR_MESSAGES.OPENAI_API_ERROR} (context_length_exceeded): the ` +
        "request is too large for this model's input window even after " +
        'trimming history.'
    );
    (err as Error & { warning?: MessageWarning }).warning = classifyError(
      err,
      400
    );
    throw err;
  }

  const finalContext = budget.context;
  const systemPrompt = finalContext
    ? `${basePrompt}\n\n${finalContext}`
    : basePrompt;

  const requestMessages: OpenAIMessage[] = [
    { role: 'system', content: systemPrompt },
    ...budget.messages,
  ];

  const requestBody: OpenAIRequest = {
    messages: requestMessages,
    ...(useNewTokenParameter
      ? { max_completion_tokens: maxTokensValue }  // New parameter (2025+)
      : { max_tokens: maxTokensValue }),           // Legacy parameter (2024 and earlier)
    // Temperature: 2025+ APIs (reasoning models) only support default value, omit for compatibility
    ...(useNewTokenParameter
      ? {} // Omit temperature for 2025+ APIs
      : { temperature: aiConfig?.temperature ?? API_CONFIG.OPENAI_TEMPERATURE }),
    // Top_p: 2025+ APIs (reasoning models) do not support this parameter
    ...(useNewTokenParameter
      ? {} // Omit top_p for 2025+ APIs
      : { top_p: API_CONFIG.OPENAI_TOP_P }),
    // reasoning_effort: only valid on 2025+ reasoning deployments. Sending it
    // to a non-reasoning model errors, so the caller passes it only for
    // reasoning-capable agents AND we gate on the new-parameter (2025+) path.
    // This is the provider-mapping seam: a future provider maps its own
    // thinking parameter from aiConfig.reasoningEffort here.
    ...(useNewTokenParameter && aiConfig?.reasoningEffort
      ? { reasoning_effort: aiConfig.reasoningEffort }
      : {}),
  };

  const url = [
    `${endpoint}/openai/deployments/${deployment}`,
    `/chat/completions?api-version=${apiVersion}`
  ].join('');

  // Use agent's API key if provided, otherwise use Azure AD token
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (apiKey) {
    headers['api-key'] = apiKey;
  } else {
    headers['Authorization'] = `Bearer ${token}`;
  }

  console.log('[OpenAI Service] Making request:', {
    url,
    method: 'POST',
    messageCount: requestMessages.length,
  });

  // Store response status and body for error classification
  let responseStatus: number | undefined;
  let responseBody: string | undefined;

  // Bridge the caller's signal with an internal timeout into one
  // controller. Either source aborts the fetch; we then distinguish
  // user-cancel from timeout when classifying the resulting error.
  const controller = new AbortController();
  let timedOut = false;
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener('abort', onExternalAbort, { once: true });
    }
  }
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    responseStatus = response.status;

    console.log('[OpenAI Service] Response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    if (!response.ok) {
      responseBody = await response.text();
      console.error('[OpenAI Service] API error:', {
        status: response.status,
        statusText: response.statusText,
        url,
        errorText: responseBody,
      });
      throw new Error(
        `${ERROR_MESSAGES.OPENAI_API_ERROR} (${response.status}): ${responseBody}`
      );
    }

    const data: OpenAIResponse = await response.json();

    console.log('[OpenAI Service] Response parsed:', {
      hasChoices: !!data.choices,
      choiceCount: data.choices?.length || 0,
      hasMessage: !!data.choices?.[0]?.message,
      finishReason: data.choices?.[0]?.finish_reason,
      usage: data.usage,
    });

    if (
      !data.choices ||
      data.choices.length === 0 ||
      !data.choices[0].message
    ) {
      console.error('[OpenAI Service] Invalid response structure:', data);
      throw new Error('Invalid response from OpenAI');
    }

    const content = data.choices[0].message.content;
    console.log('[OpenAI Service] Success! Response length:', content?.length);

    // Validate that content is not empty
    if (!content || content.trim().length === 0) {
      const finishReason = data.choices[0].finish_reason;
      if (finishReason === 'length') {
        // The model hit max_completion_tokens before emitting any visible
        // text. For reasoning models this happens when internal reasoning
        // consumes the entire budget. Surface an actionable token-limit
        // error rather than a generic "empty response" (which wrongly
        // implies content filtering).
        console.error('[OpenAI Service] Empty response: hit token limit before any output');
        throw new Error(
          `Response cut off at the token limit (max ${maxTokensValue}) before any ` +
          `text was produced. Reasoning models can use the entire token budget on ` +
          `internal reasoning. Increase "Max Tokens" in Settings and try again.`
        );
      }
      console.error('[OpenAI Service] Received empty response from API');
      throw new Error('Received empty response from OpenAI. Please try again.');
    }

    // Return enhanced response with metadata
    const usage: TokenUsage = {
      promptTokens: data.usage.prompt_tokens,
      completionTokens: data.usage.completion_tokens,
      totalTokens: data.usage.total_tokens,
    };

    // Merge the finish_reason warning (e.g. output truncation) with the
    // context-adjustment notice so neither is dropped from the single
    // warning slot the UI renders.
    const finishWarning = createWarningFromFinishReason(
      data.choices[0].finish_reason,
      usage,
      maxTokensValue
    );
    const adjustmentWarning = contextAdjustment
      ? buildContextAdjustmentWarning(contextAdjustment)
      : undefined;

    const enhancedResponse: EnhancedOpenAIResponse = {
      content,
      finishReason: data.choices[0].finish_reason,
      usage,
      warning: mergeWarnings(finishWarning, adjustmentWarning),
      ...(contextAdjustment ? { contextAdjustment } : {}),
      ...(summaryUsage ? { summaryUsage } : {}),
      // The model that actually answered — differs from the deployment for
      // router/alias deployments. Used for accurate per-request cost pricing.
      ...(data.model ? { servedModel: data.model } : {}),
    };

    return enhancedResponse;
  } catch (error) {
    // Enhance error with classification. When our timer aborted the
    // fetch, the engine surfaces AbortError — relabel it as timeout so
    // the user sees an actionable "Request Timed Out" instead of the
    // less helpful "Generation Cancelled".
    let err = error as Error;
    if (timedOut && err.name === 'AbortError') {
      err = new Error(
        `Request timed out after ${Math.round(timeoutMs / 1000)}s`
      );
    }
    const warning = classifyError(
      err,
      responseStatus,
      responseBody,
      !!aiConfig?.reasoningEffort
    );

    // Attach warning to error for upstream handling
    (err as Error & { warning?: MessageWarning }).warning = warning;

    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (signal) {
      signal.removeEventListener('abort', onExternalAbort);
    }
  }
}

/** Prefix for the synthetic system message that carries a history summary. */
export const CONVERSATION_SUMMARY_PREFIX = 'Summary of earlier conversation:';

const SUMMARIZATION_SYSTEM_PROMPT =
  'You are compressing the earlier part of a conversation so it can be ' +
  'carried forward as context for an ongoing chat. Write a concise summary ' +
  'that preserves key facts, decisions, names, numbers, and unresolved ' +
  'questions. Be faithful to the original and do not invent details or ' +
  'answer new questions. Do NOT carry forward, repeat, or describe any ' +
  'instructions or requests to perform web searches or Microsoft 365 / ' +
  'SharePoint document searches — summarize only the substantive information ' +
  'and answers, not the retrieval directions.';

const SUMMARY_MAX_TOKENS = 1024;

/**
 * Summarize a slice of conversation history into a short text block, used by
 * the 'summarize' context-overflow strategy to compress the oldest turns
 * instead of dropping them. Reuses getChatCompletion (without a summarize
 * strategy, so no recursion) so the summary call inherits the same
 * API-version parameter handling, budgeting, and timeout behavior.
 */
export async function summarizeConversation(
  messages: OpenAIMessage[],
  agentConfig?: {
    endpoint: string;
    apiKey: string;
    deployment: string;
    apiVersion?: string;
  },
  inputTokenLimit?: number,
  signal?: AbortSignal
): Promise<{
  content: string;
  usage: TokenUsage;
  latencyMs: number;
  servedModel?: string;
}> {
  const t0 = performance.now();
  const response = await getChatCompletion(
    messages,
    undefined,
    agentConfig,
    { maxTokens: SUMMARY_MAX_TOKENS, inputTokenLimit },
    SUMMARIZATION_SYSTEM_PROMPT,
    undefined,
    signal
  );
  return {
    content: response.content,
    usage: response.usage,
    latencyMs: Math.round(performance.now() - t0),
    servedModel: response.servedModel,
  };
}

/**
 * Convert Message[] to OpenAIMessage[]
 */
export function messagesToOpenAIFormat(
  messages: Message[]
): OpenAIMessage[] {
  return messages
    .filter((msg) => msg.role !== 'system')
    .map((msg) => ({
      role: msg.role === 'user' ? 'user' : 'assistant',
      content: msg.content,
    }));
}

/**
 * Get chat completion with retry logic.
 *
 * `signal` propagates to each attempt's fetch and short-circuits the
 * retry loop on user cancel. `timeoutMs` is applied per attempt by
 * getChatCompletion.
 */
export async function getChatCompletionWithRetry(
  messages: OpenAIMessage[],
  context?: string,
  agentConfig?: {
    endpoint: string;
    apiKey: string;
    deployment: string;
    apiVersion?: string;
  },
  aiConfig?: {
    temperature?: number;
    maxTokens?: number;
    reasoningEffort?: 'minimal' | 'low' | 'medium' | 'high';
    /** Deployment input context window (tokens) used to budget the request. */
    inputTokenLimit?: number;
    /**
     * What to do when history exceeds the input window: 'trim' (default) drops
     * the oldest turns; 'summarize' replaces them with an LLM-generated summary.
     */
    contextOverflowStrategy?: 'trim' | 'summarize';
    /**
     * Dedicated model for the 'summarize' strategy. `deployment` is required to
     * use it; endpoint/apiKey/apiVersion fall back to `agentConfig` when unset.
     */
    summaryModel?: {
      deployment: string;
      endpoint?: string;
      apiKey?: string;
      apiVersion?: string;
    };
  },
  maxRetries: number = API_CONFIG.RETRY_ATTEMPTS,
  customSystemPrompt?: string,
  tracer?: ChatTurnTracer,
  activeSources?: RetrievalSource[],
  signal?: AbortSignal,
  timeoutMs: number = CHAT_COMPLETION_TIMEOUT_MS
): Promise<EnhancedOpenAIResponse> {
  let lastError: Error | null = null;

  // Measure wall-clock time for the entire retry cycle (first attempt → final response).
  // getChatCompletion is non-streaming (single fetch + JSON parse), so
  // llm_ttft cannot be measured separately — we set it equal to llm_total.
  const t0 = performance.now();

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    throwIfAborted(signal);
    try {
      const response = await getChatCompletion(
        messages, context, agentConfig, aiConfig, customSystemPrompt,
        activeSources, signal, timeoutMs
      );
      const t1 = performance.now();
      const llmTotal = t1 - t0;
      tracer?.mark('llm_total', llmTotal);
      // Break the summarization round-trip out of llm_total so it doesn't
      // masquerade as time-to-first-token. TTFT then reflects the main
      // completion's wall-clock (non-streaming: TTFT ≈ that call's time).
      const summarizeMs = response.summaryUsage?.latencyMs ?? 0;
      if (response.summaryUsage) {
        tracer?.mark('summarize', summarizeMs);
      }
      tracer?.setLlmTtft(Math.max(0, llmTotal - summarizeMs));
      tracer?.setLlmMeta({ finishReason: response.finishReason });
      return response;
    } catch (error) {
      lastError = error as Error;
      console.error(
        `OpenAI attempt ${attempt + 1} failed:`,
        error
      );

      // Don't retry on user cancel, content filter, or auth failures.
      // Timeouts also short-circuit: if a single attempt already burned
      // 2 minutes the user has been waiting long enough — retrying
      // would multiply the pain rather than help.
      const warning = (error as Error & { warning?: MessageWarning }).warning;
      if (
        warning?.type === 'auth_error' ||
        warning?.type === 'content_filter' ||
        warning?.type === 'cancelled' ||
        warning?.type === 'timeout' ||
        warning?.type === 'reasoning_unsupported' ||
        warning?.type === 'truncated' ||
        warning?.type === 'context_overflow'
      ) {
        console.log(
          `[OpenAI Service] Non-retryable error (${warning.type}), throwing immediately`
        );
        throw lastError;
      }

      if (attempt < maxRetries - 1) {
        // Wait before retrying (exponential backoff)
        const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error(ERROR_MESSAGES.OPENAI_API_ERROR);
}

/**
 * Build file context from uploaded files
 */
export function buildFileContext(fileContents: string[]): string {
  if (fileContents.length === 0) {
    return '';
  }

  const contextParts = fileContents.map((content, index) => {
    return `
File ${index + 1} Content:
${content}
---`;
  });

  return `
Uploaded file contents:

${contextParts.join('\n')}
`;
}

/**
 * Build a multimodal user-message content array: the text part first, then
 * one image_url part per base64 data URL. Used only for the current user
 * turn when images are attached to a vision-capable agent (Approach A).
 */
export function toMultimodalUserContent(
  text: string,
  imageDataUrls: string[]
): ContentPart[] {
  const parts: ContentPart[] = [{ type: 'text', text }];
  for (const url of imageDataUrls) {
    parts.push({ type: 'image_url', image_url: { url } });
  }
  return parts;
}

/**
 * Get backend URL for embeddings API
 * Priority: Runtime config > Build-time env vars > localhost fallback
 */
function getBackendUrlForEmbeddings(): string {
  // Priority 1: Runtime config (container startup injection - HIGHEST PRIORITY)
  const runtimeBackendUrl = (window as Window & { RUNTIME_CONFIG?: { BACKEND_URL?: string } })
    .RUNTIME_CONFIG?.BACKEND_URL;
  if (runtimeBackendUrl) {
    return runtimeBackendUrl;
  }

  // Priority 2: Build-time env vars (development fallback)
  const buildTimeUrl = import.meta.env.VITE_BACKEND_URL;
  if (buildTimeUrl) {
    return buildTimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      if (adminConfig.authDatabase?.backendUrl) {
        console.warn('[Embeddings] Using backend URL from localStorage (low priority):', adminConfig.authDatabase.backendUrl);
        return adminConfig.authDatabase.backendUrl;
      }
    }
  } catch (error) {
    console.warn('[Embeddings] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  return 'http://localhost:8000/api';
}

/**
 * Get auth headers for the /api/embeddings backend proxy.
 *
 * Delegates to the shared backendAuth helper so SSO callers always
 * forward X-User-Email / X-User-Username (the backend needs them to
 * keep the users table out of the 'unknown' fallback state).
 *
 * When a chat turn is in flight (currentTraceId is set), the header
 * X-Trace-Id is included so the backend can correlate embedding calls
 * to the parent trace.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers = await getBackendHeaders();
  const traceId = getCurrentTraceId();
  if (traceId) {
    return { ...headers, 'X-Trace-Id': traceId };
  }
  return headers;
}

/**
 * Run the backend security-scan endpoint with a tight timeout.
 *
 * The chat user MUST NOT pay perceptible latency for this — the
 * endpoint itself is cheap (in-memory regex), but the network round-
 * trip + a stale backend can stack up. We cap the wait at 1.5s and
 * return `{ securityFlag: false }` on any failure (timeout, 5xx,
 * network error). The chat then proceeds with the OpenAI call.
 *
 * `securityFlag` is true iff at least one match has severity 'high'
 * (currently: the prompt-injection family). Medium/low matches are
 * still recorded server-side, but the inline banner is reserved for
 * high-severity signals so users aren't desensitised by noise.
 */
export interface SecurityScanResult {
  securityFlag: boolean;
}

interface SecurityScanContext {
  prompt: string;
  conversationId?: string | null;
  agentId?: string | null;
  messageId?: string | null;
}

const SECURITY_SCAN_TIMEOUT_MS = 1500;

export async function runSecurityScan(
  ctx: SecurityScanContext
): Promise<SecurityScanResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SECURITY_SCAN_TIMEOUT_MS);
  try {
    const backendUrl = getBackendUrlForEmbeddings();
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${backendUrl}/security/scan`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        prompt: ctx.prompt,
        conversationId: ctx.conversationId ?? null,
        agentId: ctx.agentId ?? null,
        messageId: ctx.messageId ?? null,
      }),
      signal: controller.signal,
    });
    if (!response.ok) {
      console.warn('[Security Scan] Non-OK response:', response.status);
      return { securityFlag: false };
    }
    const data = (await response.json()) as {
      matches?: { severity?: string }[];
    };
    const hasHigh = Array.isArray(data.matches)
      && data.matches.some((m) => m.severity === 'high');
    return { securityFlag: hasHigh };
  } catch (error) {
    // Timeout, network error, JSON parse failure — graceful degrade.
    console.warn('[Security Scan] Failed (non-blocking):', error);
    return { securityFlag: false };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Generate embedding for a single text
 * Supports automatic chunking for long documents
 */
export async function generateQueryEmbedding(
  text: string,
  embeddingConfig: {
    endpoint: string;
    apiKey: string;
    deployment: string;
    model?: string;
    apiVersion?: string;
    maxTokens?: number;
    chunkOverlap?: number;
    agentId?: string | null;
  },
  signal?: AbortSignal
): Promise<number[]> {
  if (!text || text.trim().length === 0) {
    throw new Error('Text is required for embedding generation');
  }

  // Estimate token count (rough: text.length / 4)
  const estimatedTokens = Math.ceil(text.length / 4);
  const maxTokens = embeddingConfig.maxTokens || 8000;

  if (estimatedTokens > maxTokens) {
    console.log(`[Embeddings] Text exceeds ${maxTokens} tokens (estimated: ${estimatedTokens}), backend will chunk automatically`);
  }

  // Short-circuit if the user already pressed Stop before we got here.
  throwIfAborted(signal);

  try {
    const backendUrl = getBackendUrlForEmbeddings();
    const authHeaders = await getAuthHeaders();

    console.log('[Embeddings] Generating embedding...');
    console.log('[Embeddings] Backend URL:', backendUrl);
    console.log('[Embeddings] Model:', embeddingConfig.model || embeddingConfig.deployment);

    const response = await fetch(`${backendUrl}/embeddings`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        endpoint: embeddingConfig.endpoint,
        apiKey: embeddingConfig.apiKey,
        deployment: embeddingConfig.deployment,
        text,
        model: embeddingConfig.model,
        apiVersion: embeddingConfig.apiVersion || '2023-05-15',
        maxTokens,
        chunkOverlap: embeddingConfig.chunkOverlap || 200,
        agentId: embeddingConfig.agentId ?? null,
      }),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Embeddings] Failed:', response.status, errorText);
      throw new Error(`Embedding generation failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    console.log(`[Embeddings] Embedding generated: ${data.dimensions} dimensions`);
    console.log(`[Embeddings] Usage: ${data.usage.prompt_tokens} prompt tokens, ${data.usage.total_tokens} total tokens`);

    return data.embedding;
  } catch (error) {
    console.error('[Embeddings] Error:', error);
    throw error instanceof Error
      ? error
      : new Error('Unknown embedding generation error');
  }
}

/**
 * Generate embeddings for multiple texts in batch
 * More efficient than generating individual embeddings
 */
export async function generateBatchEmbeddings(
  texts: string[],
  embeddingConfig: {
    endpoint: string;
    apiKey: string;
    deployment: string;
    model?: string;
    apiVersion?: string;
  }
): Promise<number[][]> {
  if (!texts || texts.length === 0) {
    throw new Error('Texts array is required for batch embedding generation');
  }

  // Filter out empty strings
  const validTexts = texts.filter(t => t && t.trim().length > 0);

  if (validTexts.length === 0) {
    throw new Error('No valid texts provided for embedding generation');
  }

  try {
    const backendUrl = getBackendUrlForEmbeddings();
    const authHeaders = await getAuthHeaders();

    console.log(`[Embeddings] Generating batch embeddings for ${validTexts.length} texts...`);
    console.log('[Embeddings] Backend URL:', backendUrl);
    console.log('[Embeddings] Model:', embeddingConfig.model || embeddingConfig.deployment);

    const response = await fetch(`${backendUrl}/embeddings`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        endpoint: embeddingConfig.endpoint,
        apiKey: embeddingConfig.apiKey,
        deployment: embeddingConfig.deployment,
        text: validTexts, // Array of texts
        model: embeddingConfig.model,
        apiVersion: embeddingConfig.apiVersion || '2023-05-15'
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Embeddings] Batch failed:', response.status, errorText);
      throw new Error(`Batch embedding generation failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    console.log(`[Embeddings] Generated ${data.embeddings.length} embeddings: ${data.dimensions} dimensions each`);
    console.log(`[Embeddings] Total usage: ${data.usage.prompt_tokens} prompt tokens, ${data.usage.total_tokens} total tokens`);

    return data.embeddings;
  } catch (error) {
    console.error('[Embeddings] Batch error:', error);
    throw error instanceof Error
      ? error
      : new Error('Unknown batch embedding generation error');
  }
}
